import { z } from "zod";
import { JOB_ALERT_FREQUENCIES, JOB_ALERT_EXPERIENCES } from "@artha/types";

// Sort options enum for job queries
export const JobSortBySchema = z.enum(["most_relevant", "newest", "high_cpa"]);
export type JobSortByDto = z.infer<typeof JobSortBySchema>;

// Query parameters for fetching jobs
export const JobQueryParamsSchema = z.object({
    limit: z.coerce.number().int().min(1).max(100).optional().default(10),
    offset: z.coerce.number().int().min(0).optional().default(0),
    location: z.string().optional(),
    q: z.string().optional(),
    keyword: z.string().optional(),
    keywords: z.string().optional(), // Alternative parameter name
    title: z.string().optional(),
    company: z.string().optional(),
    state: z.string().optional(),
    city: z.string().optional(),
    exp_min: z.string().optional(),
    exp_max: z.string().optional(),
    exp_level: z.string().optional(), // Experience level
    salary_min: z.coerce.number().optional(),
    salary_max: z.coerce.number().optional(),
    posted_after: z.string().optional(),
    job_type: z.string().optional(),
    work_mode: z.string().optional(),
    education: z.string().optional(),
    industry: z.string().optional(),
    exclude_keywords: z.string().optional(),
    category: z.string().optional(),
    categories: z.string().optional(), // Add categories parameter
    niche_keywords: z.string().optional(), // Add niche_keywords parameter
    negative_keywords: z.string().optional(), // Add negative_keywords parameter
    only_enriched: z.coerce.boolean().optional(),
    sort_by: JobSortBySchema.optional().default("most_relevant"), // Add sort_by parameter
    dedupBy: z.string().optional().default("null"),
    dedup_by: z.string().optional(), // Alternative parameter name
});

export type JobQueryParamsDto = z.infer<typeof JobQueryParamsSchema>;

// Single job item from external API
export const JobItemSchema = z.object({
    id: z.string(),
    title: z.string(),
    slug: z.string().optional().nullable(),
    company: z.string(),
    location: z.string().optional(),
    description: z.string().optional(),
    logo: z.string().optional(),
    posted_date: z.string(),
    job_type: z.string().optional().nullable(),
    salary_min: z.number().optional().nullable(),
    salary_max: z.number().optional().nullable(),
    salary_curr: z.string().optional().nullable(),
    exp_min: z.number().optional().nullable(),
    exp_max: z.number().optional().nullable(),
    exp_unit: z.string().optional().nullable(),
    cpc_value: z.number().optional().nullable(),
    cpa_value: z.number().optional().nullable(),
    apply_url: z.string().optional(),
    url: z.string().optional(),
    aggregator_name: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    state: z.string().optional().nullable(),
    country: z.string().optional().nullable(),
    ref_id: z.string().optional().nullable(),
    aggregator_id: z.string().optional().nullable(),
    category: z.array(z.string()).optional().default([]),
    skills: z.array(z.string()).optional().default([]),
    keywords: z.array(z.string()).optional().default([]),
    feed_id: z.string().optional().nullable(),
});

export type JobItemDto = z.infer<typeof JobItemSchema>;

// Retrieval metadata returned alongside job results
// variant: "pulse" | "control" | "pulse_fallback" (PULSE retrieval A/B experiment)
export const JobsResponseMetadataSchema = z
    .object({
        variant: z.string().optional(),
    })
    .passthrough();

export type JobsResponseMetadataDto = z.infer<typeof JobsResponseMetadataSchema>;

// Response from jobs API
export const JobsResponseSchema = z.object({
    items: z.array(JobItemSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    has_more: z.boolean().optional(),
    metadata: JobsResponseMetadataSchema.optional(),
});

export type JobsResponseDto = z.infer<typeof JobsResponseSchema>;

export const JobAlertFrequencySchema = z.enum(JOB_ALERT_FREQUENCIES);
export type JobAlertFrequencyDto = z.infer<typeof JobAlertFrequencySchema>;

export const JobAlertManualTriggerRequestSchema = z.object({
    frequency: JobAlertFrequencySchema,
    email: z.string().trim().email().transform((value) => value.toLowerCase()).optional(),
});
export type JobAlertManualTriggerRequestDto = z.infer<
    typeof JobAlertManualTriggerRequestSchema
>;

export const JobAlertExperienceSchema = z.enum(JOB_ALERT_EXPERIENCES);
export type JobAlertExperienceDto = z.infer<typeof JobAlertExperienceSchema>;

export const PublicJobAlertSubscriptionRequestSchema = z.object({
    email: z
        .string()
        .trim()
        .regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format")
        .transform((value) => value.toLowerCase()),
    frequency: JobAlertFrequencySchema,
    experience: JobAlertExperienceSchema,
    timezone: z.string().optional(),
});

export type PublicJobAlertSubscriptionRequestDto = z.infer<
    typeof PublicJobAlertSubscriptionRequestSchema
>;

export const PublicJobAlertSubscriptionResponseSchema = z.object({
    creatorId: z.string(),
    creatorUsername: z.string(),
    externalId: z.string().uuid(),
    frequency: JobAlertFrequencySchema.optional(),
    experience: JobAlertExperienceSchema.optional(),
    isActive: z.boolean(),
    unsubscribeAt: z.string().nullable(),
    subscribedAt: z.string(),
    timezone: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export type PublicJobAlertSubscriptionResponseDto = z.infer<
    typeof PublicJobAlertSubscriptionResponseSchema
>;

export const PublicJobAlertAudienceSubscriptionSchema = z.object({
    creatorId: z.string(),
    creatorUsername: z.string(),
    creatorDisplayName: z.string().optional(),
    creatorAvatarUrl: z.string().optional(),
    externalId: z.string().uuid(),
    frequency: JobAlertFrequencySchema.optional(),
    experience: JobAlertExperienceSchema.optional(),
    isActive: z.boolean(),
    unsubscribeAt: z.string().nullable(),
    subscribedAt: z.string(),
    timezone: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});

export type PublicJobAlertAudienceSubscriptionDto = z.infer<
    typeof PublicJobAlertAudienceSubscriptionSchema
>;

export const PublicJobAlertAudienceResponseSchema = z.object({
    subscriptions: z.array(PublicJobAlertAudienceSubscriptionSchema),
});

export type PublicJobAlertAudienceResponseDto = z.infer<
    typeof PublicJobAlertAudienceResponseSchema
>;

// ============================================
// Filter Options DTOs (matching artha-index API)
// ============================================

// Single filter option with value and count
export const FilterOptionSchema = z.object({
    value: z.string(),
    count: z.number(),
});

export type FilterOptionDto = z.infer<typeof FilterOptionSchema>;

// Experience level option with year range
export const ExperienceLevelOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    min_years: z.number(),
    max_years: z.number().nullable().optional(),
    count: z.number(),
});

export type ExperienceLevelOptionDto = z.infer<typeof ExperienceLevelOptionSchema>;

// Salary range info (min/max bounds from job data)
export const SalaryRangeInfoSchema = z.object({
    min_salary: z.number(),
    max_salary: z.number(),
    currency: z.string().optional().default("USD"),
    jobs_with_salary: z.number(),
});

export type SalaryRangeInfoDto = z.infer<typeof SalaryRangeInfoSchema>;

// Complete filters response from artha-index /jobs/filters
export const JobFiltersResponseSchema = z.object({
    categories: z.array(FilterOptionSchema).optional().default([]),
    job_types: z.array(FilterOptionSchema).optional().default([]),
    experience_levels: z.array(ExperienceLevelOptionSchema).optional().default([]),
    education_levels: z.array(FilterOptionSchema).optional().default([]),
    work_modes: z.array(FilterOptionSchema).optional().default([]),
    countries: z.array(FilterOptionSchema).optional().default([]),
    states: z.array(FilterOptionSchema).optional().default([]),
    cities: z.array(FilterOptionSchema).optional().default([]),
    companies: z.array(FilterOptionSchema).optional().default([]),
    industries: z.array(FilterOptionSchema).optional().default([]),
    salary_ranges: SalaryRangeInfoSchema.optional().nullable(),
    total_jobs: z.number().optional().default(0),
    query_time_ms: z.number().optional().default(0),
});

export type JobFiltersResponseDto = z.infer<typeof JobFiltersResponseSchema>;

// Request params for filters endpoint
export const JobFiltersRequestSchema = z.object({
    categories: z.string().optional(),
    niche_keywords: z.string().optional(),
    location: z.string().optional(),
    industry: z.string().optional(),
    company: z.string().optional(),
    include_categories: z.coerce.boolean().optional().default(true),
    include_job_types: z.coerce.boolean().optional().default(true),
    include_work_modes: z.coerce.boolean().optional().default(true),
    include_countries: z.coerce.boolean().optional().default(false),
    include_states: z.coerce.boolean().optional().default(false),
    include_cities: z.coerce.boolean().optional().default(false),
    include_education_levels: z.coerce.boolean().optional().default(true),
    include_experience_levels: z.coerce.boolean().optional().default(true),
    include_companies: z.coerce.boolean().optional().default(true),
    include_industries: z.coerce.boolean().optional().default(true),
});

export type JobFiltersRequestDto = z.infer<typeof JobFiltersRequestSchema>;

// Job count response from artha-index /jobs/board-search-count
export const JobCountResponseSchema = z.object({
    count: z.number(),
    query_time_ms: z.number().optional(),
});

export type JobCountResponseDto = z.infer<typeof JobCountResponseSchema>;

// Request params for job count endpoint
export const JobCountRequestSchema = z.object({
    categories: z.string().optional().default("software-development"), // Make optional with default
    niche_keywords: z.string().optional(),
    q: z.string().optional(),
    location: z.string().optional(),
    job_type: z.string().optional(),
    work_mode: z.string().optional(),
    salary_min: z.coerce.number().optional(),
    salary_max: z.coerce.number().optional(),
    education: z.string().optional(),
    industry: z.string().optional(),
    company: z.string().optional(),
    exp_level: z.string().optional(),
    sort_by: JobSortBySchema.optional().default("most_relevant"), // Add sort_by parameter
});

export type JobCountRequestDto = z.infer<typeof JobCountRequestSchema>;


// ============================================
// Unified Job Index Parameters (for shared utils)
// ============================================

/**
 * Job index parameters input schema (flexible format)
 * Accepts both arrays and CSV strings for multi-value fields
 * Used as input to buildJobIndexParams function
 */
export const JobIndexParamsInputSchema = z.object({
    categories: z.union([z.string(), z.array(z.string())]).optional(),
    niche_keywords: z.union([z.string(), z.array(z.string())]).optional(),
    location: z.union([z.string(), z.array(z.string())]).optional(),
    job_type: z.union([z.string(), z.array(z.string())]).optional(),
    work_mode: z.union([z.string(), z.array(z.string())]).optional(),
    exp_level: z.union([z.string(), z.array(z.string())]).optional(),
    education: z.union([z.string(), z.array(z.string())]).optional(),
    industry: z.union([z.string(), z.array(z.string())]).optional(),
    company: z.union([z.string(), z.array(z.string())]).optional(),
    salary_min: z.number().optional(),
    salary_max: z.number().optional(),
    sort_by: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    offset: z.number().int().min(0).optional(),
    dedup_by: z.string().optional(),
    posted_after: z.string().optional(),
});

export type JobIndexParamsInputDto = z.infer<typeof JobIndexParamsInputSchema>;

/**
 * Job index parameters output schema (normalized format)
 * All multi-value fields are normalized to CSV strings
 * This is the output of buildJobIndexParams function
 */
export const JobIndexParamsSchema = z.object({
    categories: z.string().optional(),
    niche_keywords: z.string().optional(),
    location: z.string().optional(),
    job_type: z.string().optional(),
    work_mode: z.string().optional(),
    exp_level: z.string().optional(),
    education: z.string().optional(),
    industry: z.string().optional(),
    company: z.string().optional(),
    salary_min: z.number().optional(),
    salary_max: z.number().optional(),
    sort_by: z.string().optional(),
    limit: z.number().int().min(1).max(100).optional(),
    offset: z.number().int().min(0).optional(),
    dedup_by: z.string().optional(),
    posted_after: z.string().optional(),
});

export type JobIndexParamsDto = z.infer<typeof JobIndexParamsSchema>;
